import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader, FormField, Input, PrimaryButton, SecondaryButton, Select } from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import { fetchAdminFeedbacks } from "../utils/adminApi";

const DEFAULT_LIMIT = 50;

const formatIsoDateTime = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return isoString;
  return d.toLocaleString();
};

const SITUATION_OPTIONS = [
  { value: "", label: "Any" },
  { value: "PUBLIC_FEEDBACK_PAGE", label: "PUBLIC_FEEDBACK_PAGE" },
  { value: "EDITOR_FEEDBACK_MODAL", label: "EDITOR_FEEDBACK_MODAL" },
  { value: "GENERAL", label: "GENERAL" },
  { value: "CANCELLATION", label: "CANCELLATION" },
  { value: "MARKETING_UNSUBSCRIBE", label: "MARKETING_UNSUBSCRIBE" },
  { value: "BUG_REPORT", label: "BUG_REPORT" },
  { value: "FEATURE_REQUEST", label: "FEATURE_REQUEST" },
];

const RATING_OPTIONS = [
  { value: "", label: "Any" },
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
];

const FeedbacksAdminPage = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    user: "",
    email: "",
    name: "",
    rating: "",
    situation: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const [feedbacks, setFeedbacks] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const hasAnyFilter = useMemo(() => {
    return Object.values(appliedFilters || {}).some((v) => String(v || "").trim().length > 0);
  }, [appliedFilters]);

  const loadFeedbacks = async ({ reset = false, cursor = null } = {}) => {
    if (reset) {
      setLoading(true);
      setError("");
    } else {
      setLoadingMore(true);
      setError("");
    }

    try {
      const data = await fetchAdminFeedbacks({
        cursor,
        limit: DEFAULT_LIMIT,
        user: (appliedFilters.user || "").trim(),
        email: (appliedFilters.email || "").trim(),
        name: (appliedFilters.name || "").trim(),
        rating: (appliedFilters.rating || "").trim(),
        situation: (appliedFilters.situation || "").trim(),
      });

      const results = data?.results || [];
      const next = data?.next_cursor || null;

      if (reset) setFeedbacks(results);
      else setFeedbacks((prev) => [...(prev || []), ...results]);

      setNextCursor(next);
    } catch (err) {
      setError(err?.message || "Failed to load feedbacks");
      if (reset) {
        setFeedbacks([]);
        setNextCursor(null);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadFeedbacks({ reset: true, cursor: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const onApply = (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setAppliedFilters({ ...filters });
  };

  const onReset = () => {
    const cleared = { user: "", email: "", name: "", rating: "", situation: "" };
    setFilters(cleared);
    setAppliedFilters(cleared);
  };

  return (
    <div className={`min-h-screen ${surfaces.modernApp}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className={`${typography.h2} ${textColors.primary}`}>Feedbacks</h1>
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>
              Browse feedback submissions with filters and cursor pagination.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <SecondaryButton
              onClick={() => loadFeedbacks({ reset: true, cursor: null })}
              disabled={loading}
              className="w-full sm:w-auto whitespace-nowrap"
            >
              {loading ? "Refreshing..." : "Refresh"}
            </SecondaryButton>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-2xl border border-red-300 bg-red-50 text-red-700 px-4 py-3">
            {error}
          </div>
        )}

        <Card className="mb-6">
          <CardHeader title="Filters" subtitle={hasAnyFilter ? "Filters applied" : "No filters applied"} />
          <CardBody>
            <form onSubmit={onApply} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <FormField label="User" htmlFor="filter_user">
                  <Input
                    id="filter_user"
                    type="text"
                    value={filters.user}
                    onChange={(e) => setFilters((p) => ({ ...p, user: e.target.value }))}
                    placeholder="id / username / email"
                  />
                </FormField>

                <FormField label="Email" htmlFor="filter_email">
                  <Input
                    id="filter_email"
                    type="text"
                    value={filters.email}
                    onChange={(e) => setFilters((p) => ({ ...p, email: e.target.value }))}
                    placeholder="contains…"
                  />
                </FormField>

                <FormField label="Name" htmlFor="filter_name">
                  <Input
                    id="filter_name"
                    type="text"
                    value={filters.name}
                    onChange={(e) => setFilters((p) => ({ ...p, name: e.target.value }))}
                    placeholder="contains…"
                  />
                </FormField>

                <FormField label="Rating" htmlFor="filter_rating">
                  <Select
                    id="filter_rating"
                    value={filters.rating}
                    onChange={(e) => setFilters((p) => ({ ...p, rating: e.target.value }))}
                    options={RATING_OPTIONS}
                    placeholder={null}
                  />
                </FormField>

                <FormField label="Situation" htmlFor="filter_situation">
                  <Select
                    id="filter_situation"
                    value={filters.situation}
                    onChange={(e) => setFilters((p) => ({ ...p, situation: e.target.value }))}
                    options={SITUATION_OPTIONS}
                    placeholder={null}
                  />
                </FormField>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <PrimaryButton type="submit" disabled={loading} className="w-full sm:w-auto whitespace-nowrap">
                  Apply
                </PrimaryButton>
                <SecondaryButton
                  type="button"
                  onClick={onReset}
                  disabled={loading}
                  className="w-full sm:w-auto whitespace-nowrap"
                >
                  Reset
                </SecondaryButton>
              </div>
            </form>
          </CardBody>
        </Card>

        <Card variant="glass">
          <CardHeader
            title="All Feedbacks"
            subtitle={`Loaded: ${feedbacks.length}${nextCursor ? " • More available" : ""}`}
          />
          <CardBody>
            {loading ? (
              <div className={`${typography.body} ${textColors.secondary}`}>Loading feedbacks…</div>
            ) : feedbacks.length === 0 ? (
              <div className={`${typography.body} ${textColors.secondary}`}>No feedbacks found.</div>
            ) : (
              <>
                {/* Mobile + iPad: cards (no horizontal scroll) */}
                <div className="space-y-3 lg:hidden">
                  {feedbacks.map((fb) => {
                    const u = fb?.user || null;
                    const userLabel = u ? `${u.username || "User"} (#${u.id})` : "—";

                    const email = (fb?.email || "").trim();
                    const name = (fb?.name || "").trim();
                    const situation = (fb?.situation || "").trim();
                    const rating = fb?.rating;
                    const message = fb?.message || "";

                    const title =
                      email || name || (fb?.id != null ? `Feedback #${fb.id}` : "Feedback");

                    const subtitleParts = [];
                    if (name && title !== name) subtitleParts.push(name);
                    if (fb?.id != null && title !== `Feedback #${fb.id}`) subtitleParts.push(`#${fb.id}`);
                    subtitleParts.push(formatIsoDateTime(fb?.created_at));
                    const subtitle = subtitleParts.filter(Boolean).join(" • ");

                    const showRating = rating != null && String(rating).trim() !== "";
                    const showSituation = !!situation;

                    const headerKeys = new Set();
                    if (email && title === email) headerKeys.add("email");
                    if (name && (title === name || subtitleParts.includes(name))) headerKeys.add("name");
                    if (showRating) headerKeys.add("rating");
                    if (showSituation) headerKeys.add("situation");

                    const details = [
                      {
                        key: "user",
                        label: "User",
                        spanClass: "",
                        render: () =>
                          u ? (
                            <button
                              type="button"
                              className={`underline ${textColors.primary} block min-w-0 truncate`}
                              title={userLabel}
                              onClick={() => navigate(`/admin/dashboard/users/${u.id}`)}
                            >
                              {userLabel}
                            </button>
                          ) : (
                            <span className={textColors.muted}>—</span>
                          ),
                      },
                      {
                        key: "email",
                        label: "Email",
                        spanClass: "",
                        render: () => (
                          <div className="min-w-0 truncate" title={email}>
                            {email || "—"}
                          </div>
                        ),
                      },
                      {
                        key: "name",
                        label: "Name",
                        spanClass: "",
                        render: () => (
                          <div className="min-w-0 truncate" title={name}>
                            {name || "—"}
                          </div>
                        ),
                      },
                      {
                        key: "message",
                        label: "Message",
                        spanClass: "sm:col-span-2",
                        render: () =>
                          message ? (
                            <div className="break-words whitespace-pre-wrap line-clamp-4" title={message}>
                              {message}
                            </div>
                          ) : (
                            <span className={textColors.muted}>—</span>
                          ),
                      },
                    ];

                    const visibleDetails = details.filter((d) => !headerKeys.has(d.key));

                    return (
                      <div
                        key={fb.id}
                        className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div
                              className={`${typography.body} ${textColors.primary} font-medium truncate`}
                              title={title || "Feedback"}
                            >
                              {title || "Feedback"}
                            </div>
                            {subtitle ? (
                              <div className={`${typography.caption} ${textColors.muted} mt-0.5`}>{subtitle}</div>
                            ) : null}
                          </div>

                          {showRating || showSituation ? (
                            <div className="flex flex-wrap gap-2 sm:justify-end">
                              {showRating ? (
                                <div
                                  className={[
                                    "px-2 py-1 rounded-2xl",
                                    "border",
                                    "whitespace-nowrap",
                                    typography.caption,
                                    surfaces.glassCard,
                                    borders.glass,
                                    textColors.secondary,
                                  ].join(" ")}
                                >
                                  Rating: {rating}
                                </div>
                              ) : null}
                              {showSituation ? (
                                <div
                                  className={[
                                    "px-2 py-1 rounded-2xl",
                                    "border",
                                    "whitespace-nowrap",
                                    typography.caption,
                                    surfaces.glassCard,
                                    borders.glass,
                                    textColors.secondary,
                                  ].join(" ")}
                                >
                                  {situation}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>

                        {visibleDetails.length > 0 ? (
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {visibleDetails.map((d) => (
                              <div key={d.key} className={d.spanClass}>
                                <div className={`${typography.caption} ${textColors.muted}`}>{d.label}</div>
                                <div className={`${typography.bodySmall} ${textColors.primary} mt-0.5 min-w-0`}>
                                  {d.render()}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                {/* Desktop: table */}
                <div className="hidden lg:block overflow-x-auto">
                <table className={`w-full text-left ${typography.bodySmall}`}>
                  <thead>
                    <tr className={`border-b ${borders.glass}`}>
                      <th className="py-2 pr-4">Created</th>
                      <th className="py-2 pr-4">User</th>
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Rating</th>
                      <th className="py-2 pr-4">Situation</th>
                      <th className="py-2">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedbacks.map((fb) => {
                      const u = fb?.user || null;
                      const userLabel = u ? `${u.username || "User"} (#${u.id})` : "—";
                      return (
                        <tr key={fb.id} className={`border-b ${borders.glass}`}>
                          <td className="py-2 pr-4 whitespace-nowrap">{formatIsoDateTime(fb.created_at)}</td>
                          <td className="py-2 pr-4">
                            {u ? (
                              <button
                                type="button"
                                className={`underline ${textColors.primary}`}
                                onClick={() => navigate(`/admin/dashboard/users/${u.id}`)}
                              >
                                {userLabel}
                              </button>
                            ) : (
                              <span className={textColors.muted}>—</span>
                            )}
                          </td>
                          <td className="py-2 pr-4">{fb.email || "—"}</td>
                          <td className="py-2 pr-4">{fb.name || "—"}</td>
                          <td className="py-2 pr-4">{fb.rating ?? "—"}</td>
                          <td className="py-2 pr-4">{fb.situation || "—"}</td>
                          <td className="py-2">
                            <div className="max-w-[520px] truncate" title={fb.message || ""}>
                              {fb.message || "—"}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}

            {nextCursor && (
              <div className="mt-4 flex items-center justify-center">
                <SecondaryButton
                  onClick={() => loadFeedbacks({ reset: false, cursor: nextCursor })}
                  disabled={loadingMore}
                  className="w-full sm:w-auto whitespace-nowrap"
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </SecondaryButton>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default FeedbacksAdminPage;

